import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Save,
  Trash2,
  MapPin,
  Phone,
  Clock,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { apiGet, apiPut } from '../lib/api'

type Sucursal = {
  id: string
  nombre: string
  direccion: string
  ciudad: string
  estado: string
  telefono: string
  horario: string
  lat: string
  lng: string
}

const CONFIG_KEY = 'Sitio_Sucursales'

function emptySucursal(): Sucursal {
  return {
    id: crypto.randomUUID(),
    nombre: '',
    direccion: '',
    ciudad: '',
    estado: '',
    telefono: '',
    horario: '',
    lat: '',
    lng: '',
  }
}

function parseSucursales(raw: string): Sucursal[] {
  if (!raw) return []
  try {
    const obj = JSON.parse(raw)
    const arr = Array.isArray(obj) ? obj : obj?.sucursales
    if (!Array.isArray(arr)) return []
    return arr.map((s: any) => ({
      id: String(s.id ?? crypto.randomUUID()),
      nombre: String(s.nombre ?? ''),
      direccion: String(s.direccion ?? ''),
      ciudad: String(s.ciudad ?? ''),
      estado: String(s.estado ?? ''),
      telefono: String(s.telefono ?? ''),
      horario: String(s.horario ?? ''),
      lat: String(s.lat ?? ''),
      lng: String(s.lng ?? ''),
    }))
  } catch {
    return []
  }
}

export function Sucursales() {
  const qc = useQueryClient()
  const [items, setItems] = useState<Sucursal[]>([])
  const [original, setOriginal] = useState<Sucursal[]>([])
  const [flash, setFlash] = useState<{ tipo: 'ok' | 'err'; msg: string } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-config', CONFIG_KEY],
    queryFn: () =>
      apiGet<{ data: Record<string, string> }>(`/api/admin/config?keys=${CONFIG_KEY}`),
  })

  useEffect(() => {
    if (data?.data) {
      const parsed = parseSucursales(data.data[CONFIG_KEY] ?? '')
      setItems(parsed)
      setOriginal(parsed)
    }
  }, [data])

  const dirty = useMemo(() => JSON.stringify(items) !== JSON.stringify(original), [items, original])

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload = JSON.stringify({ sucursales: items })
      return apiPut<{ updated: number }>('/api/admin/config', {
        changes: { [CONFIG_KEY]: payload },
      })
    },
    onSuccess: () => {
      setFlash({ tipo: 'ok', msg: 'Sucursales guardadas' })
      setOriginal(items)
      qc.invalidateQueries({ queryKey: ['admin-config', CONFIG_KEY] })
      setTimeout(() => setFlash(null), 3000)
    },
    onError: (e: any) => setFlash({ tipo: 'err', msg: e?.message || 'Error al guardar' }),
  })

  function add() {
    setItems((arr) => [...arr, emptySucursal()])
  }
  function remove(id: string) {
    setItems((arr) => arr.filter((s) => s.id !== id))
  }
  function update(id: string, patch: Partial<Sucursal>) {
    setItems((arr) => arr.map((s) => (s.id === id ? { ...s, ...patch } : s)))
  }

  return (
    <>
      <Topbar
        title="Sucursales"
        subtitle="Puntos físicos de Maz Autopartes"
        actions={
          <>
            {dirty && <Badge variant="warning">sin guardar</Badge>}
            <button
              type="button"
              onClick={add}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-100"
            >
              <Plus className="size-3.5" /> Agregar sucursal
            </button>
            <button
              type="button"
              onClick={() => saveMut.mutate()}
              disabled={!dirty || saveMut.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {saveMut.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
              Guardar cambios
            </button>
          </>
        }
      />

      {flash && (
        <div
          className={`mx-6 mt-4 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
            flash.tipo === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {flash.tipo === 'ok' ? <CheckCircle2 className="size-4" /> : <AlertCircle className="size-4" />}
          <span>{flash.msg}</span>
        </div>
      )}

      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center gap-2 text-ink-500">
            <Loader2 className="size-4 animate-spin" /> Cargando sucursales…
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-ink-200 bg-white p-10 text-center">
            <MapPin className="mx-auto size-8 text-ink-400" />
            <h3 className="mt-3 text-sm font-semibold text-ink-900">Sin sucursales cargadas</h3>
            <p className="mt-1 text-xs text-ink-500">Agregá tu primera sucursal con los datos físicos del local.</p>
            <button
              type="button"
              onClick={add}
              className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
            >
              <Plus className="size-3.5" /> Agregar sucursal
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {items.map((s) => (
              <SucursalCard key={s.id} sucursal={s} onChange={(p) => update(s.id, p)} onDelete={() => remove(s.id)} />
            ))}
          </div>
        )}
      </div>
    </>
  )
}

function SucursalCard({
  sucursal,
  onChange,
  onDelete,
}: {
  sucursal: Sucursal
  onChange: (p: Partial<Sucursal>) => void
  onDelete: () => void
}) {
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="grid size-9 place-items-center rounded-lg bg-brand-50 text-brand-600">
            <MapPin className="size-4" />
          </div>
          <div>
            <div className="text-sm font-semibold text-ink-900">
              {sucursal.nombre || 'Sucursal sin nombre'}
            </div>
            <div className="text-xs text-ink-500">
              {[sucursal.ciudad, sucursal.estado].filter(Boolean).join(', ') || 'Sin ubicación'}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onDelete}
          className="rounded-lg p-1.5 text-red-600 hover:bg-red-50"
          title="Eliminar"
        >
          <Trash2 className="size-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Field label="Nombre" value={sucursal.nombre} onChange={(v) => onChange({ nombre: v })} />
        <Field label="Teléfono" icon={<Phone className="size-3" />} value={sucursal.telefono} onChange={(v) => onChange({ telefono: v })} />
        <div className="sm:col-span-2">
          <Field label="Dirección" value={sucursal.direccion} onChange={(v) => onChange({ direccion: v })} />
        </div>
        <Field label="Ciudad" value={sucursal.ciudad} onChange={(v) => onChange({ ciudad: v })} />
        <Field label="Estado" value={sucursal.estado} onChange={(v) => onChange({ estado: v })} />
        <div className="sm:col-span-2">
          <Field label="Horario" icon={<Clock className="size-3" />} value={sucursal.horario} onChange={(v) => onChange({ horario: v })} placeholder="Lun a Vie 9:00–18:00" />
        </div>
        <Field label="Latitud" value={sucursal.lat} onChange={(v) => onChange({ lat: v })} mono />
        <Field label="Longitud" value={sucursal.lng} onChange={(v) => onChange({ lng: v })} mono />
      </div>
    </div>
  )
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  icon,
  mono,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  icon?: React.ReactNode
  mono?: boolean
}) {
  return (
    <div>
      <label className="mb-1 flex items-center gap-1 text-xs font-semibold text-ink-700">
        {icon} {label}
      </label>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 ${
          mono ? 'font-mono text-xs' : ''
        }`}
      />
    </div>
  )
}
